import { cn } from "@/libs/utils";
import { useTrans } from "@/utils/useTrans";

type Row = {
  titleKey: string;
  dk2: string;
  dk3: string;
  dk3Accent?: boolean;
};

const rows: Row[] = [
  { titleKey: "Fingertips", dk2: "LRA × 5", dk3: "LRA × 5" },
  { titleKey: "Palm", dk2: "—", dk3: "LRA × 2", dk3Accent: true },
  { titleKey: "Wrist", dk2: "LRA × 1", dk3: "VCM × 1", dk3Accent: true },
  {
    titleKey: "Total haptic points",
    dk2: "6 points",
    dk3: "8 points",
    dk3Accent: true,
  },
];

export default function Dk3Comparison() {
  const { t } = useTrans(["tactsuit"]);

  return (
    <section className="w-full bg-white px-4">
      <div className="mx-auto flex w-full max-w-screen-max flex-col items-center gap-6 laptop:gap-10">
        {/* Header */}
        <header className="flex max-w-[708px] flex-col items-center gap-6 text-center laptop:gap-4">
          <h2 className="text-2xl font-400 leading-8 text-coolgray-90 laptop:text-[32px] laptop:leading-10">
            <span className="laptop:hidden">
              {t("Sharper. Richer.")}
              <br />
              {t("The New DK3.")}
            </span>
            <span className="hidden laptop:inline">
              {t("Sharper. Richer. The New DK3.")}
            </span>
          </h2>
          <p className="text-center text-base font-400 leading-[22px] tracking-[0.5px] text-coolgray-60">
            {t(
              "Palm and wrist now deliver entirely new sensations — together creating a fuller, more refined haptic experience across the whole hand.",
            )}
          </p>
        </header>

        {/* Table — items-center 로 DK3 col 의 p-4 인셋만큼 다른 cols 가운데 정렬 */}
        <div className="flex w-full max-w-[952px] items-center rounded-2xl border border-coolgray-20 bg-white p-4 laptop:gap-6 laptop:p-6">
          {/* Title column */}
          <div className="flex flex-1 flex-col laptop:w-[240px] laptop:flex-none">
            <CellHeader>&nbsp;</CellHeader>
            {rows.map((row) => (
              <Cell
                key={row.titleKey}
                className="text-base font-500 leading-[22px] tracking-[0.15px] text-coolgray-90 laptop:justify-end laptop:pr-2 laptop:text-right"
              >
                {t(row.titleKey)}
              </Cell>
            ))}
          </div>

          {/* DK2 column */}
          <div className="flex flex-1 flex-col">
            <CellHeader className="text-coolgray-60">DK2</CellHeader>
            {rows.map((row) => (
              <Cell
                key={row.titleKey}
                className="justify-center text-center text-base font-400 leading-[22px] tracking-[0.5px] text-coolgray-60"
              >
                {row.dk2}
              </Cell>
            ))}
          </div>

          {/* DK3 column (highlighted) */}
          <div className="flex flex-1 flex-col rounded-2xl bg-coolgray-10 p-4">
            <CellHeader className="text-coolgray-90">DK3</CellHeader>
            {rows.map((row) => (
              <Cell
                key={row.titleKey}
                className={cn(
                  "justify-center text-center text-base font-400 leading-[22px] tracking-[0.5px]",
                  row.dk3Accent ? "text-blue-60" : "text-coolgray-90",
                )}
              >
                {row.dk3}
              </Cell>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

const CellHeader = ({
  children,
  className,
}: React.PropsWithChildren<{ className?: string }>) => (
  <div
    className={cn(
      "flex h-16 items-center justify-center text-center text-2xl font-400 leading-8 laptop:h-20 laptop:text-[28px] laptop:leading-9",
      className,
    )}
  >
    {children}
  </div>
);

const Cell = ({
  children,
  className,
}: React.PropsWithChildren<{ className?: string }>) => (
  <div className={cn("flex h-16 w-full items-center laptop:h-20", className)}>
    {children}
  </div>
);
